import {
  getRegistryRequestScope,
  scopedTemplateKey,
  subscribeRegistryOrganizationScope
} from '#~/device/registry/registry-config-cache.js';
import { useDepot } from '#~/depot/hooks.js';
import { useSyncExternalStore } from 'react';
import { isControlDevice } from '#~/device/index.js';
import { ConfigBadgeStatus } from './types.js';
import { deriveSetupAttention, isGenericDriver } from '#~/device/setup-attention.js';
import { useTransitoryDevice } from '#~/device/transitory.js';
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { shallow } from 'zustand/shallow';
import { locateProxy } from '#~/agent/index.js';
import { useAgents } from '#~/agent/hooks.js';
import {
  StatusCalibrationState,
  StatusDeviceConnectionStatus
} from '@tryiris-ai/common.protojs/agent/v1/agent';

/**
 * Computes which configuration badge (if any) should be shown for a device.
 *
 * Priority (highest to lowest):
 * 1. Authenticate — credentials required
 * 2. Select model — no model identified, or a listed brand's generic model
 * 3. Connect controls — driver set but PTZ not connected
 * 4. Calibrate camera — a pan/tilt camera that was never calibrated
 *
 * Returns `undefined` if no configuration badge is needed.
 */
export const useConfigBadge = (
  deviceId: Device['id'],
  roomId?: Room['id']
): T_Maybe<ConfigBadgeStatus> => {
  const agentId = locateProxy(deviceId)?.id;
  const agent = useAgents((a) => a.find((a) => a.id === agentId));
  const safeRoomId = roomId ?? agent?.roomId;

  const placeholder = useTransitoryDevice(deviceId);
  const proxy = locateProxy(deviceId);
  const statusDevice = proxy?.state.status?.device[deviceId];
  const movementConnectionStatus = statusDevice?.movement?.connectionStatus?.status;

  const orgScope = useSyncExternalStore(
    subscribeRegistryOrganizationScope,
    getRegistryRequestScope
  );

  return useDepot((µ) => {
    const device = safeRoomId ? µ.rooms[safeRoomId]?.devices[deviceId] : undefined;
    const template = device?.modelId
      ? µ.registry.templates[scopedTemplateKey(orgScope, device.modelId)]
      : undefined;
    // Read mapped auth inside the reactive selector, including frontend-only placeholders.
    const setup = deriveSetupAttention(device ?? placeholder, template?.model, statusDevice);
    if (setup === 'authentication') return ConfigBadgeStatus.AUTHENTICATE;
    if (!device || device.agentStatus === 'offline' || device.agentStatus === 'offline-embedded')
      return undefined;
    // Embedded cameras manage their own drivers and controls; those settings tabs are hidden.
    if (proxy?.embedded) return undefined;
    if (setup === 'driver') return ConfigBadgeStatus.SELECT_DRIVER;

    // 3. Connect controls: has a control device but controls aren't connected
    if (isControlDevice(device)) {
      if (
        movementConnectionStatus !== StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED &&
        movementConnectionStatus !== undefined
      ) {
        return ConfigBadgeStatus.CONNECT_CONTROLS;
      }
    }

    // An accepted "Other – brand is not listed" driver has no calibration profile to run.
    if (isGenericDriver(device.modelId, template?.model)) return undefined;

    // 4. Calibrate camera: the device reported a real "never calibrated" status.
    //    Per absent-data-gates-permissively (ai.knowledge/global): an absent
    //    (undefined) calibration status is "unknown yet" — the add/reload window
    //    before the encoder reports — and must NOT nag. pbToJsControlCalibration
    //    keeps an explicit CALIBRATION_STATE_NULL distinguishable from absence,
    //    so a genuinely-never-calibrated device still reaches the loaded-non-OK
    //    check below and shows the badge. A generic/"other" model returned at
    //    priority 2 or at the guard above, so it never reaches here.
    if (isControlDevice(device) && device.modelId) {
      const calibrationStatus = device.control?.calibration?.status;
      // Calibration drives PTZ auto-tracking, which needs absolute pan AND tilt, yet the
      // encoder still reports NULL for a camera it cannot calibrate. Same capability the
      // sidebar indicator, settings Calibrate button and calibration watchdog check, read
      // via its depot mapping so this selector re-runs when the report changes.
      const capabilities = device.control?.capabilities;
      const canCalibrate = !!capabilities?.pan?.absolute && !!capabilities?.tilt?.absolute;

      // Calibration status is a StatusCalibrationState enum value. Show only for
      // a loaded, non-OK status (CALIBRATION_STATE_NULL = loaded, never
      // calibrated). Undefined (unknown) is intentionally excluded so the
      // add/reload window stays quiet; RUNNING/FAIL are owned by the runtime
      // badges, not this config badge.
      if (
        // Per rule: absent-data-gates-permissively (ai.knowledge/global) — an
        // unreported capability is unknown, not pan/tilt: never nag on it.
        canCalibrate &&
        // Per rule: absent-data-gates-permissively (ai.knowledge/global) — an
        // unfetched template is "not known yet", not "uncalibrated".
        template !== undefined &&
        calibrationStatus !== undefined &&
        calibrationStatus !== StatusCalibrationState.CALIBRATION_STATE_OK &&
        calibrationStatus !== StatusCalibrationState.CALIBRATION_STATE_SUCCESS &&
        calibrationStatus !== StatusCalibrationState.CALIBRATION_STATE_OK_GENERIC &&
        // Don't show during active calibration or failure (runtime badges handle those)
        calibrationStatus !== StatusCalibrationState.CALIBRATION_STATE_RUNNING &&
        calibrationStatus !== StatusCalibrationState.CALIBRATION_STATE_FAIL
      ) {
        return ConfigBadgeStatus.CALIBRATE_CAMERA;
      }
    }

    return undefined;
  }, shallow);
};
